import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { keepTimesWhole } from '../messages';
import { IconButton, ToastBox, ToastText } from '../Operations.styles';

import { XMarkIcon } from './icons';

/** toasts stay 5 s (the prototype's 4 s is overruled by the ticket). */
export const TOAST_MS = 5000;

type Toast = { id: number; title: string; body?: string };

export type ShowToast = (title: string, body?: string) => void;

const ToastContext = createContext<ShowToast>(() => {});

/** `showToast(title, body?)` for anything inside the Operations page. */
export const useOpsToast = (): ShowToast => useContext(ToastContext);

type ViewProps = { toast: Toast | null; onClose: () => void };

// UI spec §10: title + body, close icon, top right. No variants; errors use
// the same box. The live region stays mounted so each toast is announced.
export const OpsToastView = ({ toast, onClose }: ViewProps) => (
  <div role="status" aria-live="polite">
    {toast && (
      <ToastBox key={toast.id} data-testid="opsToast">
        <ToastText>
          <strong data-testid="opsToastTitle">
            {keepTimesWhole(toast.title)}
          </strong>
          {toast.body && (
            <span data-testid="opsToastBody">{keepTimesWhole(toast.body)}</span>
          )}
        </ToastText>
        <IconButton
          type="button"
          aria-label="Close"
          data-testid="opsToastCloseButton"
          onClick={onClose}
          css={{ width: '24px', height: '24px', '& svg': { width: '24px' } }}
        >
          <XMarkIcon />
        </IconButton>
      </ToastBox>
    )}
  </div>
);

/** One toast at a time: a new one replaces the last and restarts the timer. */
export const OpsToastProvider = ({ children }: { children: ReactNode }) => {
  const [toast, setToast] = useState<Toast | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const seq = useRef(0);

  const close = useCallback(() => {
    clearTimeout(timer.current);
    setToast(null);
  }, []);

  const showToast = useCallback<ShowToast>((title, body) => {
    clearTimeout(timer.current);
    seq.current += 1;
    setToast({ id: seq.current, title, body });
    timer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  const value = useMemo(() => showToast, [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <OpsToastView toast={toast} onClose={close} />
    </ToastContext.Provider>
  );
};
